import React from 'react';
import {
  Button,
  Badge,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aaas/shared';
import { ExternalLink, Loader2, MoreHorizontal, Plus, Sparkles, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Marketplace app representation, agnostic of the source (subscribed / available).
 */
export interface MarketplaceApp {
  id: string;
  slug: string;
  name: string;
  description: string;
  is_active: boolean;
  is_beta: boolean;
  version: string;
}

export type AppCardState = 'subscribed' | 'available' | 'coming-soon';

interface AppCardProps {
  app: MarketplaceApp;
  state: AppCardState;
  /** Loading flag for current action — disables buttons while in-flight. */
  busy?: boolean;
  /** Show seat-management option in subscribed dropdown (team workspace only). */
  canAssignSeats?: boolean;
  /** Show admin actions (subscribe / unsubscribe) — owner-only. */
  canManage?: boolean;
  onOpen?: (app: MarketplaceApp) => void;
  onSubscribe?: (app: MarketplaceApp) => void;
  onUnsubscribe?: (app: MarketplaceApp) => void;
  onAssignSeats?: (app: MarketplaceApp) => void;
}

/**
 * Stable hue derived from the slug — keeps the icon swatch consistent across
 * renders without needing an `icon_url` field.
 */
const slugHue = (slug: string): number => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
};

const AppIcon: React.FC<{ slug: string; name: string }> = ({ slug, name }) => {
  const hue = slugHue(slug);
  const initial = (name || slug).trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-base font-semibold text-white"
      style={{ backgroundColor: `hsl(${hue}, 55%, 50%)` }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
};

export const AppCard: React.FC<AppCardProps> = ({
  app,
  state,
  busy = false,
  canAssignSeats = false,
  canManage = false,
  onOpen,
  onSubscribe,
  onUnsubscribe,
  onAssignSeats,
}) => {
  const { t } = useTranslation('portal');
  const isSubscribed = state === 'subscribed';
  const isComingSoon = state === 'coming-soon';

  return (
    <Card
      className={[
        'flex h-full flex-col transition-colors',
        isSubscribed ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200',
        isComingSoon ? 'opacity-60' : 'hover:border-gray-300',
      ].join(' ')}
    >
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        {/* Top row: icon + name + state marker */}
        <div className="flex items-start gap-3">
          <AppIcon slug={app.slug} name={app.name} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h4 className="truncate text-base font-semibold text-gray-900">{app.name}</h4>
              {isSubscribed && (
                <Badge className="bg-blue-600 text-white hover:bg-blue-600 text-xs">
                  {t('appDashboard.subscribed', { defaultValue: 'Đã đăng ký' })}
                </Badge>
              )}
            </div>
            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{app.description}</p>
          </div>
        </div>

        {/* Meta row: version · beta · coming soon */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="font-mono">v{app.version}</span>
          {app.is_beta && (
            <>
              <span aria-hidden="true">·</span>
              <span className="text-orange-600">
                {t('appDashboard.betaTag', { defaultValue: 'Beta' })}
              </span>
            </>
          )}
          {isComingSoon && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1 text-amber-600">
                <Sparkles className="h-3 w-3" />
                {t('appDashboard.comingSoon', { defaultValue: 'Sắp ra mắt' })}
              </span>
            </>
          )}
        </div>

        {/* Spacer pushes actions to the bottom for equal height */}
        <div className="flex-1" />

        {/* Action row */}
        {isComingSoon ? (
          <Button variant="outline" size="sm" disabled className="w-full">
            {t('appDashboard.comingSoon', { defaultValue: 'Sắp ra mắt' })}
          </Button>
        ) : isSubscribed ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onOpen?.(app)}
            >
              <ExternalLink className="mr-1 h-4 w-4" />
              {t('appDashboard.openApp')}
            </Button>
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2"
                    disabled={busy}
                    aria-label={t('appDashboard.moreActions', { defaultValue: 'Thao tác khác' })}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MoreHorizontal className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canAssignSeats && (
                    <>
                      <DropdownMenuItem onClick={() => onAssignSeats?.(app)}>
                        <Users className="mr-2 h-4 w-4" />
                        {t('appDashboard.assignSeat')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={() => onUnsubscribe?.(app)}
                    className="text-red-600 focus:bg-red-50 focus:text-red-700"
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('appDashboard.unsubscribe', { defaultValue: 'Hủy đăng ký' })}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            disabled={busy || !canManage}
            onClick={() => onSubscribe?.(app)}
          >
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            {t('appDashboard.subscribe')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
